'use client';

import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import posthog from '@/lib/posthog';
import { ProviderIcon } from '../OnboardingIcons';
import { useSession } from 'next-auth/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import { togglePRBotEnabled } from '@/apis/togglePRBotEnabled';
import {
  configurePRBotForOrgGitlab,
  configurePRBotForOrgBitbucket,
} from '@/apis/prReviewsConfig';
import { toast } from '@/utils/toastManager';
import { useGitHubPermissions } from '@/hooks/useGitHubPermissions';
import { useUserLogged } from '@/hooks/useUserLogged';
import { githubInstallUrl } from '@/lib/utils/githubInstallUrl';
import { isRepoInOrgScope } from '@/lib/utils/gitOrgScope';
import { usePaginatedRepositories } from '@/hooks/useUnifiedCodebaseManagement';
import { useCodebaseReposInfinite } from '@/hooks/useCodebaseRepos';
import OnboardingRepoSyncGate from '@/components/OnboardingRepoSyncGate';
import type { UnifiedRepository } from '@/apis/unifiedCodebaseManagement';

export type ChooseReposEventPrefix = 'team_insight' | 'code_review';

interface ChooseRepositoriesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userUUID: string;
  orgUUID: string;
  onUpdated?: () => void;
  /** When PR bot is not installed for a GitLab repo, call with repo URL (e.g. to open GitLab install modal). */
  onOpenGitLabInstall?: (repoUrl: string) => void;
  /** Prefix for PostHog events (team_insight vs code_review). When omitted, events are not sent. */
  eventPrefix?: ChooseReposEventPrefix;
  /** Bitbucket: restrict the repo listing to these project keys (same as parent's bbScopeKeysForUnified). */
  bitbucketProjectKeys?: string[];
  /** Git org name/id to validate repo scope against and to build the
   * GitHub PR-bot install URL for. Defaults to the caller's own home org
   * (organization?.GitOrgName/GitOrgId from useUserLogged()) -- the
   * correct default for every existing call site, which always operates
   * on the caller's own workspace. Pass these explicitly when orgUUID is
   * NOT the caller's home org (e.g. cross-org combining: picking repos
   * for an independently-owned org just linked into the caller's view). */
  targetGitOrgName?: string;
  targetGitOrgId?: string;
}

function isPRBotNotInstalledError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('pr bot installation not found') ||
    lower.includes('not found for repository')
  );
}

function userFacingRepositoryError(message: string): string {
  const lower = message.toLowerCase();
  if (
    lower.includes('not installed') ||
    lower.includes('installation not found') ||
    lower.includes('not found for repository')
  ) {
    return 'PR bot is not installed for this repository.';
  }
  if (
    lower.includes('unauthorized') ||
    lower.includes('403') ||
    lower.includes('401')
  ) {
    return 'You are not authorized to change this setting.';
  }
  return 'Something went wrong. Please try again.';
}

export function ChooseRepositoriesModal({
  open,
  onOpenChange,
  userUUID,
  orgUUID,
  onUpdated,
  onOpenGitLabInstall,
  eventPrefix,
  bitbucketProjectKeys,
  targetGitOrgName,
  targetGitOrgId,
}: ChooseRepositoriesModalProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [togglingRepo, setTogglingRepo] = useState<string | null>(null);
  const [groupAccessToken, setGroupAccessToken] = useState('');
  const { data: session } = useSession();
  const { user, organization, status: userStatus } = useUserLogged();
  const { validateAndProceed } = useGitHubPermissions();
  const sessionProvider = session?.user?.provider;
  const isGitLabProvider =
    sessionProvider === 'gitlab' || user?.provider === 'gitlab';
  const isGitLabUserReady = !isGitLabProvider || userStatus !== 'loading';
  const isGitLab = sessionProvider !== 'github';
  const isBitbucket = sessionProvider === 'bitbucket';
  // orgUUID isn't always the caller's own home org (see targetGitOrgName's
  // doc) -- resolve repo-scope validation and the install URL against the
  // org this modal is actually managing, not always the caller's own.
  // Gate the pair together on targetGitOrgName's presence, not fall back to
  // home per-field independently -- a caller in override mode whose target
  // org happens to have no numeric GitHub id (targetGitOrgId undefined)
  // must NOT silently pick up the HOME org's id instead.
  const isTargetOrgOverride = targetGitOrgName !== undefined;
  // Only the cross-org path (a caller-supplied target org) can legitimately
  // reach a permanently-empty list: the normal onboarding flow arrives here
  // after the App install, so an empty list there means something else and
  // must keep its existing copy rather than send the user off to reinstall.
  const needsGithubAppInstall =
    isTargetOrgOverride && sessionProvider === 'github';
  const effectiveGitOrgName = isTargetOrgOverride
    ? targetGitOrgName
    : organization?.GitOrgName;
  const effectiveGitOrgId = isTargetOrgOverride
    ? targetGitOrgId
    : organization?.GitOrgId;

  // 300 ms debounce for server-side search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Bitbucket keeps the provider-backed listing (project-key scoping has no
  // DB equivalent); GitHub/GitLab read from the DB-backed listing behind the
  // repo sync gate.
  const bitbucketQuery = usePaginatedRepositories(orgUUID, userUUID, {
    pageSize: 50,
    enabled: open && !!orgUUID && isBitbucket,
    sortBy: 'enabled_first',
    search: debouncedSearch,
    bitbucketProjectKeys,
    // Bitbucket onboarding intentionally lists only repos where the PR bot
    // is already installed — preserve that constraint from the prior client
    // filter (r.isPRBotEnabled || r.hasPRBot).
    prBotOnly: sessionProvider === 'bitbucket',
  });
  const dbQuery = useCodebaseReposInfinite(orgUUID, userUUID, {
    pageSize: 50,
    // Keep cache warm while closed — reopen must not re-list / re-sync.
    enabled: !!orgUUID && !isBitbucket,
    sortBy: 'enabled_first',
    search: debouncedSearch,
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
  });
  const {
    data,
    isLoading: reposLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    totalCount,
    apiError: reposApiError,
  } = isBitbucket ? bitbucketQuery : dbQuery;

  const allRepos = useMemo(() => {
    // Widen: bitbucket/DB page shapes differ, both carry repositories.
    const pages: Array<{ repositories: UnifiedRepository[] }> | undefined =
      data?.pages;
    return pages?.flatMap((p) => p.repositories) ?? [];
  }, [data?.pages]);

  // Infinite-scroll sentinel
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    const scrollEl = scrollContainerRef.current;
    const sentinel = loadMoreRef.current;
    if (!scrollEl || !sentinel || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { root: scrollEl, rootMargin: '100px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, allRepos.length]);

  const oauthProviderForIcon = useMemo(() => {
    const fromUser = user?.provider?.trim();
    const fromSession = (
      session?.user as { provider?: string } | undefined
    )?.provider?.trim();
    if (fromUser) return fromUser;
    if (fromSession) return fromSession;
    if (user?.GitlabUsername || user?.glUserName) return 'gitlab';
    if (user?.GithubUsername || user?.ghUserName) return 'github';
    return undefined;
  }, [
    user?.provider,
    user?.GitlabUsername,
    user?.glUserName,
    user?.GithubUsername,
    user?.ghUserName,
    session?.user,
  ]);

  useEffect(() => {
    if (open && eventPrefix) {
      posthog.capture(`${eventPrefix}_choose_repos_modal_viewed`, {
        org_uuid: orgUUID,
        repo_count: totalCount,
      });
    }
  }, [open, eventPrefix, orgUUID, totalCount]);

  const handlePRBotNotInstalled = useCallback(
    async (repo: UnifiedRepository) => {
      // Outside the workspace's git org there is nothing to install: the install
      // page only offers this org's account, and the backend rejects any other.
      if (!isRepoInOrgScope(repo.name, effectiveGitOrgName)) {
        toast.show({
          title: 'Outside your connected org',
          description: `This workspace connects repositories from ${effectiveGitOrgName || 'its connected git org'} only.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (sessionProvider === 'github') {
        validateAndProceed(() => {
          // Hand queued analytics to the SDK before the page unloads.
          const target = githubInstallUrl(
            '/onboarding?step=repository-team-review',
            effectiveGitOrgId
          );
          void posthog.ready().then(() => {
            window.location.href = target;
          });
        });
        return;
      }

      if (isGitLabProvider) {
        if (!isGitLabUserReady) {
          toast.show({
            title: 'Loading your GitLab access',
            description:
              'Please wait a moment and try again once your GitLab account finishes loading.',
            status: 'info',
            duration: 4000,
            isClosable: true,
          });
          return;
        }

        if (repo.url && user?.UserUUID) {
          const result = await configurePRBotForOrgGitlab({
            UserUUID: user.UserUUID,
            RepoUrl: repo.url,
            OrgUUID: orgUUID,
            GroupAccessToken: groupAccessToken.trim() || undefined,
          });

          if (!result.error) {
            onUpdated?.();
            toast.show({
              title: 'PR bot configured',
              description: `Configured ${repo.name}${groupAccessToken.trim() ? ' with the provided group access token' : ''}.`,
              status: 'success',
              duration: 4000,
              isClosable: true,
            });
            return;
          }

          toast.show({
            title: 'PR bot install failed',
            description: result.error,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      }

      if (onOpenGitLabInstall && repo.url) {
        onOpenGitLabInstall(repo.url);
      } else {
        toast.show({
          title: 'PR bot not installed',
          description:
            'Install the PR bot for this repository from Settings → Code management.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [
      sessionProvider,
      validateAndProceed,
      isGitLabProvider,
      isGitLabUserReady,
      user?.UserUUID,
      orgUUID,
      groupAccessToken,
      onUpdated,
      onOpenGitLabInstall,
      effectiveGitOrgId,
      effectiveGitOrgName,
    ]
  );

  const handleToggle = useCallback(
    async (repo: UnifiedRepository) => {
      if (!orgUUID || togglingRepo !== null) return;
      const repoName = repo.name;
      const nextEnabled = !repo.isPRBotEnabled;
      setTogglingRepo(repoName);
      try {
        if (isGitLab && nextEnabled) {
          if (isGitLabProvider && !isGitLabUserReady) {
            toast.show({
              title: 'Loading your GitLab access',
              description:
                'Please wait a moment and try again once your GitLab account finishes loading.',
              status: 'info',
              duration: 4000,
              isClosable: true,
            });
            return;
          }

          if (!repo.url || !user?.UserUUID) {
            throw new Error('Repository URL or user context is missing');
          }

          const res =
            sessionProvider === 'bitbucket'
              ? await configurePRBotForOrgBitbucket({
                  UserUUID: user.UserUUID,
                  RepoUrl: repo.url,
                  OrgUUID: orgUUID,
                })
              : await configurePRBotForOrgGitlab({
                  UserUUID: user.UserUUID,
                  RepoUrl: repo.url,
                  OrgUUID: orgUUID,
                  GroupAccessToken: groupAccessToken.trim() || undefined,
                });

          if (res && typeof res === 'object' && 'error' in res && res.error) {
            throw new Error(
              typeof res.error === 'string'
                ? res.error
                : 'Failed to register repository'
            );
          }
        } else {
          const result = await togglePRBotEnabled({
            repoName,
            orgUUID,
            ...(nextEnabled
              ? {
                  enable_pr_evaluation: true,
                  enable_auto_comments: true,
                  enable_documentation: true,
                  enable_security: true,
                }
              : {
                  enable_pr_evaluation: false,
                  enable_auto_comments: false,
                  enable_documentation: false,
                  enable_security: false,
                }),
          });
          if (!result.success) {
            throw new Error(result.error ?? 'Failed to update');
          }
        }

        if (eventPrefix) {
          posthog.capture(`${eventPrefix}_choose_repos_repo_toggled`, {
            repo_name: repoName,
            enabled: nextEnabled,
            org_uuid: orgUUID,
          });
        }
        onUpdated?.();
        void refetch();
        toast.show({
          title: nextEnabled ? 'Enabled' : 'Disabled',
          description: nextEnabled
            ? `All features enabled for ${repoName}`
            : `PR evaluation disabled for ${repoName}`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } catch (error) {
        const rawMsg =
          error instanceof Error ? error.message : 'Failed to update';
        if (isPRBotNotInstalledError(rawMsg)) {
          await handlePRBotNotInstalled(repo);
        } else {
          toast.show({
            title: 'Error',
            description: userFacingRepositoryError(rawMsg),
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        }
      } finally {
        setTogglingRepo(null);
      }
    },
    [
      orgUUID,
      togglingRepo,
      onUpdated,
      refetch,
      handlePRBotNotInstalled,
      eventPrefix,
      isGitLabProvider,
      isGitLabUserReady,
      isGitLab,
      user?.UserUUID,
      groupAccessToken,
      sessionProvider,
    ]
  );

  const handleEnableAll = useCallback(async () => {
    if (!orgUUID || togglingRepo !== null) return;
    const toEnable = allRepos.filter((r) => !r.isPRBotEnabled);
    if (toEnable.length === 0) return;

    if (isGitLab) {
      if (isGitLabProvider && !isGitLabUserReady) {
        toast.show({
          title: 'Loading your GitLab access',
          description:
            'Please wait a moment and try again once your GitLab account finishes loading.',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (isGitLabProvider && !user?.UserUUID) {
        toast.show({
          title: 'Update failed',
          description:
            'Your GitLab account is not ready yet. Please try again in a moment.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      const results = await Promise.allSettled(
        toEnable.map((repo) => {
          if (!repo.url) {
            return Promise.reject(
              new Error(`Repository URL not found for ${repo.name}`)
            );
          }

          if (!user?.UserUUID) {
            return Promise.reject(new Error('User context is missing'));
          }

          return sessionProvider === 'bitbucket'
            ? configurePRBotForOrgBitbucket({
                UserUUID: user.UserUUID,
                RepoUrl: repo.url,
                OrgUUID: orgUUID,
              })
            : configurePRBotForOrgGitlab({
                UserUUID: user.UserUUID,
                RepoUrl: repo.url,
                OrgUUID: orgUUID,
                GroupAccessToken: groupAccessToken.trim() || undefined,
              });
        })
      );

      const succeeded = results.filter((r) => {
        if (r.status !== 'fulfilled') return false;
        const v = r.value;
        if (v && typeof v === 'object' && 'error' in v && v.error) return false;
        return true;
      }).length;

      const firstError = results.find((r) => {
        if (r.status === 'rejected') return true;
        const v = r.value;
        return Boolean(v && typeof v === 'object' && 'error' in v && v.error);
      });
      if (eventPrefix) {
        posthog.capture(`${eventPrefix}_choose_repos_enable_all_clicked`, {
          org_uuid: orgUUID,
          repos_enabled: succeeded,
          repos_total: toEnable.length,
        });
      }
      onUpdated?.();
      if (succeeded === toEnable.length) {
        toast.show({
          title: 'Updated',
          description: `Enabled ${succeeded} repo${succeeded === 1 ? '' : 's'}.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } else if (succeeded > 0) {
        toast.show({
          title: 'Partially updated',
          description: `Enabled ${succeeded} of ${toEnable.length} repos.`,
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast.show({
          title: 'Update failed',
          description:
            firstError && firstError.status === 'fulfilled'
              ? firstError.value.error || 'Could not enable repositories.'
              : firstError && firstError.status === 'rejected'
                ? firstError.reason instanceof Error
                  ? firstError.reason.message
                  : String(firstError.reason)
                : 'Could not enable repositories.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
      return;
    }

    const results = await Promise.allSettled(
      toEnable.map((repo) =>
        togglePRBotEnabled({
          repoName: repo.name,
          orgUUID,
          enable_pr_evaluation: true,
          enable_auto_comments: true,
          enable_documentation: true,
          enable_security: true,
        })
      )
    );
    const firstNotInstalled = toEnable.find((repo, i) => {
      const r = results[i];
      if (r.status === 'fulfilled' && !r.value.success && r.value.error) {
        return isPRBotNotInstalledError(r.value.error);
      }
      if (r.status === 'rejected' && r.reason) {
        return isPRBotNotInstalledError(
          r.reason instanceof Error ? r.reason.message : String(r.reason)
        );
      }
      return false;
    });
    if (firstNotInstalled) {
      await handlePRBotNotInstalled(firstNotInstalled);
      onUpdated?.();
      return;
    }
    const succeeded = results.filter(
      (r) => r.status === 'fulfilled' && r.value.success
    ).length;
    if (eventPrefix) {
      posthog.capture(`${eventPrefix}_choose_repos_enable_all_clicked`, {
        org_uuid: orgUUID,
        repos_enabled: succeeded,
        repos_total: toEnable.length,
      });
    }
    onUpdated?.();
    if (succeeded === toEnable.length) {
      toast.show({
        title: 'Updated',
        description: `Enabled ${succeeded} repo${succeeded === 1 ? '' : 's'}.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } else if (succeeded > 0) {
      toast.show({
        title: 'Partially updated',
        description: `Enabled ${succeeded} of ${toEnable.length} repos.`,
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    } else {
      toast.show({
        title: 'Update failed',
        description: 'Could not enable repositories.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [
    orgUUID,
    allRepos,
    togglingRepo,
    onUpdated,
    handlePRBotNotInstalled,
    eventPrefix,
    isGitLabProvider,
    isGitLabUserReady,
    isGitLab,
    user?.UserUUID,
    groupAccessToken,
    sessionProvider,
  ]);

  const handleDisableAll = useCallback(async () => {
    if (!orgUUID || togglingRepo !== null) return;
    const toDisable = allRepos.filter((r) => r.isPRBotEnabled);
    if (toDisable.length === 0) return;
    const results = await Promise.allSettled(
      toDisable.map((repo) =>
        togglePRBotEnabled({
          repoName: repo.name,
          orgUUID,
          enable_pr_evaluation: false,
          enable_auto_comments: false,
          enable_documentation: false,
          enable_security: false,
        })
      )
    );
    const succeeded = results.filter(
      (r) => r.status === 'fulfilled' && r.value.success
    ).length;
    if (eventPrefix) {
      posthog.capture(`${eventPrefix}_choose_repos_disable_all_clicked`, {
        org_uuid: orgUUID,
        repos_disabled: succeeded,
        repos_total: toDisable.length,
      });
    }
    onUpdated?.();
    if (succeeded === toDisable.length) {
      toast.show({
        title: 'Updated',
        description: `Disabled ${succeeded} repo${succeeded === 1 ? '' : 's'}.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } else if (succeeded > 0) {
      toast.show({
        title: 'Partially updated',
        description: `Disabled ${succeeded} of ${toDisable.length} repos.`,
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    } else {
      toast.show({
        title: 'Update failed',
        description: 'Could not disable repositories.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [orgUUID, allRepos, togglingRepo, onUpdated, eventPrefix]);

  const handleClose = useCallback(() => {
    if (eventPrefix) {
      posthog.capture(`${eventPrefix}_choose_repos_modal_closed`, {
        org_uuid: orgUUID,
      });
    }
    setSearch('');
    onOpenChange(false);
  }, [onOpenChange, eventPrefix, orgUUID]);

  const repoCount =
    totalCount === 1
      ? '1 Repository'
      : `${totalCount > 0 ? totalCount : allRepos.length} Repositories`;

  /**
   * github/gitlab: block the repo list behind the one-shot provider→DB repo
   * sync. Bitbucket keeps the provider listing — no gate.
   */
  const withRepoSyncGate = (content: React.ReactNode) =>
    isBitbucket ? (
      content
    ) : (
      <OnboardingRepoSyncGate orgUUID={orgUUID} userUUID={userUUID}>
        {content}
      </OnboardingRepoSyncGate>
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[520px] w-full max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden border border-ds-border-subtlest bg-ds-surface-white rounded-2xl shadow-xl"
        hideCloseButton
      >
        <DialogHeader className="flex flex-row gap-2 items-start p-4 text-left shrink-0">
          <div className="flex flex-col gap-2 min-w-0 flex-1">
            <div
              className="flex shrink-0 size-8 rounded-md overflow-hidden items-center justify-center"
              aria-hidden
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                className="size-full"
              >
                <path
                  d="M27.5 3.5H4.5C3.94687 3.5 3.5 3.94687 3.5 4.5V27.5C3.5 28.0531 3.94687 28.5 4.5 28.5H27.5C28.0531 28.5 28.5 28.0531 28.5 27.5V4.5C28.5 3.94687 28.0531 3.5 27.5 3.5ZM26.25 26.25H5.75V5.75H26.25V26.25Z"
                  fill="#56653E"
                />
                <path
                  d="M5.75 26.25H26.25V5.75H5.75V26.25ZM16.3594 19.2812H22.1406C22.2687 19.2812 22.375 19.3938 22.375 19.5312V21.0312C22.375 21.1687 22.2687 21.2812 22.1406 21.2812H16.3594C16.2312 21.2812 16.125 21.1687 16.125 21.0312V19.5312C16.125 19.3938 16.2312 19.2812 16.3594 19.2812ZM9.625 19.0719C9.625 19 9.65937 18.9281 9.71562 18.8813L13.1469 16L9.71562 13.1187C9.6869 13.0961 9.66379 13.0671 9.64807 13.034C9.63235 13.001 9.62446 12.9647 9.625 12.9281V10.9688C9.625 10.7562 9.87188 10.6406 10.0344 10.7781L16.0344 15.8062C16.1562 15.9062 16.1562 16.0906 16.0344 16.1906L10.0344 21.2219C9.87188 21.3594 9.625 21.2437 9.625 21.0312V19.0719Z"
                  fill="#E7F2D4"
                />
                <path
                  d="M10.0344 21.2219L16.0344 16.1907C16.1563 16.0907 16.1563 15.9063 16.0344 15.8063L10.0344 10.7782C9.99816 10.7474 9.95386 10.7276 9.90674 10.7212C9.85962 10.7149 9.81167 10.7221 9.76857 10.7422C9.72546 10.7623 9.68902 10.7943 9.66358 10.8345C9.63813 10.8746 9.62474 10.9213 9.625 10.9688V12.9282C9.625 13.0032 9.65625 13.0719 9.71563 13.1188L13.1469 16L9.71563 18.8813C9.6877 18.9047 9.66514 18.9338 9.6495 18.9667C9.63386 18.9996 9.6255 19.0355 9.625 19.0719V21.0313C9.625 21.2438 9.87188 21.3594 10.0344 21.2219ZM16.125 21.0313C16.125 21.1688 16.2313 21.2813 16.3594 21.2813H22.1406C22.2688 21.2813 22.375 21.1688 22.375 21.0313V19.5313C22.375 19.3938 22.2688 19.2813 22.1406 19.2813H16.3594C16.2313 19.2813 16.125 19.3938 16.125 19.5313V21.0313Z"
                  fill="#56653E"
                />
              </svg>
            </div>
            <DialogTitle className="text-base font-semibold leading-[1.45] text-ds-text-darker">
              Choose Repositories
            </DialogTitle>
            <DialogDescription className="text-sm font-medium leading-[1.45] text-ds-text-subtle">
              Select where Entelligence should run. You can change this anytime.
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="shrink-0 rounded-md p-2 text-ds-text-subtle hover:text-ds-text-default hover:bg-ds-border-subtlest transition-colors focus:outline-none focus:ring-2 focus:ring-ds-border-darker focus:ring-offset-2"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </DialogHeader>

        <div
          className="h-px shrink-0 w-full bg-ds-border-subtlest"
          aria-hidden
        />

        <div className="p-4 flex flex-col gap-4 flex-1 min-h-0 overflow-hidden">
          {/* Green banner: Entelligence only analyzes PRs in enabled repos */}
          <div
            className="rounded-md px-2 py-2 bg-ds-brand-lightest border border-ds-brand-subtlest text-ds-brand-default text-sm font-medium leading-[1.45]"
            role="status"
          >
            Entelligence only analyzes pull requests in enabled repositories.
          </div>

          <div className="relative shrink-0">
            <MagnifyingGlass
              className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-ds-text-subtle pointer-events-none"
              weight="regular"
            />
            <Input
              type="search"
              placeholder="Search Repositories"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 border-ds-border-subtlest bg-ds-surface-white text-ds-text-default placeholder:text-ds-text-subtle rounded-lg"
              aria-label="Search repositories"
            />
          </div>

          {isGitLabProvider && (
            <div className="flex flex-col gap-2 rounded-xl border border-ds-border-subtlest bg-ds-surface-white p-4">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-ds-text-default">
                  GitLab group access token
                </p>
                <p className="text-xs font-medium leading-5 text-ds-text-subtle">
                  Optional, but recommended when Entelligence needs to invite
                  the tracked bot user to a GitLab group or project.
                </p>
              </div>
              <Input
                type="password"
                value={groupAccessToken}
                onChange={(e) => setGroupAccessToken(e.target.value)}
                placeholder="glpat-..."
                autoComplete="off"
                className="h-10 border-ds-border-subtlest bg-ds-surface-white text-ds-text-default placeholder:text-ds-text-subtle rounded-lg"
                aria-label="GitLab group access token"
              />
            </div>
          )}

          {withRepoSyncGate(
            <div
              ref={scrollContainerRef}
              className="flex-1 min-h-0 overflow-y-auto border border-ds-border-subtlest rounded-xl bg-ds-surface-white max-h-[360px]"
            >
              {reposLoading ? (
                <div className="py-8 flex items-center justify-center text-sm text-ds-text-subtle">
                  <Loader2 className="h-4 w-4 animate-spin mr-2 text-ds-brand-subtle" />
                  Loading repositories…
                </div>
              ) : allRepos.length === 0 ? (
                <div className="py-8 text-center text-sm text-ds-text-subtle">
                  {reposApiError ? (
                    reposApiError
                  ) : search.trim() ? (
                    'No repositories match your search.'
                  ) : needsGithubAppInstall ? (
                    // A GitHub org's repos reach the DB only through the
                    // App's installation_repositories webhook -- the backend
                    // never crawls GitHub (see sync_codebase_repos_stream).
                    // So for an org just onboarded through cross-org
                    // combining, "empty" is not an error state: nobody has
                    // installed the App on it yet, and without this the flow
                    // dead-ends on a list that can never fill.
                    <div className="flex flex-col items-center gap-3">
                      <span>
                        Entelligence is not installed on{' '}
                        <span className="ds-text-code-md text-ds-text-darker">
                          {effectiveGitOrgName}
                        </span>{' '}
                        yet, so it cannot see any repositories.
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          validateAndProceed(() => {
                            // Back to wherever this modal was opened from,
                            // not a hardcoded Settings path: it also opens
                            // during onboarding, and sending that caller to
                            // Settings drops them out of the flow they were
                            // part-way through with no way back to it.
                            window.location.href = githubInstallUrl(
                              `${window.location.pathname}${window.location.search}`,
                              effectiveGitOrgId
                            );
                          })
                        }
                      >
                        Install on {effectiveGitOrgName}
                      </Button>
                    </div>
                  ) : (
                    'No repositories found.'
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-ds-border-subtlest hover:bg-transparent">
                      <TableHead className="h-9 w-[72px] px-4 py-2.5 text-xs font-medium text-ds-text-subtle">
                        Active
                      </TableHead>
                      <TableHead className="h-9 px-4 py-2.5 text-xs font-medium text-ds-text-subtle">
                        {repoCount}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allRepos.map((repo) => (
                      <TableRow
                        key={repo.name}
                        className="border-ds-border-subtlest h-12 hover:bg-ds-border-subtlest/50"
                      >
                        <TableCell className="px-4 py-0 h-12 align-middle">
                          <Switch
                            checked={repo.isPRBotEnabled}
                            onCheckedChange={() => handleToggle(repo)}
                            disabled={togglingRepo === repo.name}
                            className="shrink-0 data-unchecked:bg-ds-border-subtlest data-checked:bg-ds-brand-subtle"
                            aria-label={`${repo.isPRBotEnabled ? 'Disable' : 'Enable'} ${repo.name}`}
                          />
                        </TableCell>
                        <TableCell className="px-4 py-0 h-12 align-middle">
                          <div className="flex gap-2 items-center min-w-0">
                            <ProviderIcon
                              provider={oauthProviderForIcon}
                              size={20}
                            />
                            <span className="text-sm font-medium text-ds-text-default truncate">
                              {repo.name}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Infinite-scroll sentinel */}
                    <TableRow
                      ref={loadMoreRef}
                      aria-hidden
                      className="border-0"
                    >
                      <TableCell
                        colSpan={2}
                        className="py-2 text-center border-0"
                      >
                        {isFetchingNextPage && (
                          <Loader2 className="h-4 w-4 animate-spin text-ds-brand-subtle mx-auto" />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </div>
          )}

          <div
            className="h-px shrink-0 w-full bg-ds-border-subtlest"
            aria-hidden
          />

          <DialogFooter className="flex flex-row justify-end gap-2 pt-0 shrink-0 sm:space-x-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="border-ds-border-subtlest text-ds-text-default hover:bg-ds-border-subtlest rounded-lg"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleClose}
              className="bg-ds-brand-default border-[1.5px] border-ds-brand-subtler text-ds-brand-subtlest hover:opacity-95 rounded-lg"
            >
              Update
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
